import React, { useState } from 'react';
import {
    AdminPanelSettings,
    CameraAlt,
    ExitToApp,
    Login,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Toolbar,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';

const DRAWER_WIDTH = 240;

interface ResponsiveDrawerProps {
    isAdmin: boolean;
    session: any; // Replace with a stricter type as needed
    children: React.ReactNode;
}

export default function ResponsiveDrawer({
    isAdmin,
    session,
    children,
}: ResponsiveDrawerProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleNavigation = (path: string) => {
        navigate(path);
        // Close drawer on mobile after a navigation
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    // Compute the page title based on the current location.
    let pageTitle = 'Dashboard';
    switch (location.pathname) {
        case '/camera':
            pageTitle = 'Camera';
            break;
        case '/admin':
            pageTitle = 'Admin Panel';
            break;
        case '/login':
            pageTitle = 'Login';
            break;
        default:
            pageTitle = 'Dashboard';
            break;
    }

    // Logout handler that calls supabase.auth.signOut()
    const handleLogout = async () => {
        const { error } = await supabase.auth.signOut({ scope: 'local' });
        if (error) {
            console.error('Error signing out:', error);
            // Optionally display an error message to the user
        } else {
            // Optionally clear any local session state here
            navigate('/login');
        }
    };

    const drawerContent = (
        <>
            <Toolbar>
                <Typography variant="h6" noWrap>
                    Pineapple Family
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                {session ? (
                    <>
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={location.pathname === '/camera'}
                                onClick={() => handleNavigation('/camera')}
                            >
                                <ListItemIcon>
                                    <CameraAlt />
                                </ListItemIcon>
                                <ListItemText primary="Camera" />
                            </ListItemButton>
                        </ListItem>
                        {isAdmin && (
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={location.pathname === '/admin'}
                                    onClick={() => handleNavigation('/admin')}
                                >
                                    <ListItemIcon>
                                        <AdminPanelSettings />
                                    </ListItemIcon>
                                    <ListItemText primary="Admin Panel" />
                                </ListItemButton>
                            </ListItem>
                        )}
                        <ListItem disablePadding>
                            <ListItemButton onClick={handleLogout}>
                                <ListItemIcon>
                                    <ExitToApp />
                                </ListItemIcon>
                                <ListItemText primary="Logout" />
                            </ListItemButton>
                        </ListItem>
                    </>
                ) : (
                    <ListItem
                        button
                        selected={location.pathname === '/login'}
                        onClick={() => handleNavigation('/login')}
                    >
                        <ListItemIcon>
                            <Login />
                        </ListItemIcon>
                        <ListItemText primary="Login" />
                    </ListItem>
                )}
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* AppBar with hamburger menu on mobile */}
            <AppBar position="fixed" sx={{ ml: { md: `${DRAWER_WIDTH}px` } }}>
                <Toolbar>
                    {isMobile && (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { md: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" noWrap component="div">
                        {pageTitle}
                    </Typography>
                </Toolbar>
            </AppBar>
            {/* The navigation drawer */}
            <Box
                component="nav"
                sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
                aria-label="navigation drawer"
            >
                {isMobile ? (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: DRAWER_WIDTH,
                            },
                        }}
                    >
                        {drawerContent}
                    </Drawer>
                ) : (
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: DRAWER_WIDTH,
                            },
                        }}
                        open
                    >
                        {drawerContent}
                    </Drawer>
                )}
            </Box>
            {/* Main content goes here */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                }}
            >
                <Toolbar /> {/* Adds spacing so content doesn't sit under the AppBar */}
                {children}
            </Box>
        </Box>
    );
}
